'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import StatusBadge from '@/components/ui/StatusBadge';
import Modal from '@/components/ui/Modal';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';
import { sendDeliveryConfirmedEmail, sendEscrowReleasedEmail } from '@/lib/emailService';

interface DeliveryDetail {
  id: string;
  orderId: string;
  route: string;
  pickupCity: string;
  destinationCity: string;
  parcelCategory: string;
  parcelDescription: string;
  weightKg: number;
  status: 'matched' | 'payment_escrowed' | 'pickup_confirmed' | 'in_transit' | 'delivered' | 'completed';
  travelerName: string;
  travelerUsername: string;
  travelerRating: number;
  travelerPhone: string;
  travelerVerified: boolean;
  travelerId: string;
  senderId: string;
  guarantorName: string;
  guarantorPhone: string;
  transportMethod: string;
  departureTime: string | null;
  estimatedArrival: string | null;
  offeredPriceKes: number;
  deliveryPin: string;
  qrCode: string;
  matchedAt: string | null;
  senderReadyConfirmedAt: string | null;
  senderCancelledAt: string | null;
  travelerPodUploadedAt: string | null;
  senderReceiptConfirmedAt: string | null;
  escrowReleasedAt: string | null;
  podPhotoUrl: string;
  deliveryPhotoUrl: string;
  arrivedAtDestinationAt: string | null;
}

const statusConfig: Record<DeliveryDetail['status'], { label: string; variant: 'info' | 'warning' | 'primary' | 'success' | 'muted' | 'danger'; step: number }> = {
  matched: { label: 'Traveler Matched', variant: 'info', step: 1 },
  payment_escrowed: { label: 'Payment Escrowed', variant: 'warning', step: 2 },
  pickup_confirmed: { label: 'Pickup Confirmed', variant: 'primary', step: 3 },
  in_transit: { label: 'In Transit', variant: 'info', step: 4 },
  delivered: { label: 'Delivered', variant: 'success', step: 5 },
  completed: { label: 'Completed', variant: 'success', step: 6 },
};

function CountdownTimer({ targetDate }: { targetDate: string | null }) {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0, expired: false });

  useEffect(() => {
    if (!targetDate) return;
    const target = new Date(targetDate).getTime();

    const tick = () => {
      const now = Date.now();
      const diff = target - now;
      if (diff <= 0) {
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0, expired: true });
        return;
      }
      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeLeft({ hours, minutes, seconds, expired: false });
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate]);

  if (!targetDate) return null;

  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <div className={`flex items-center gap-2 ${timeLeft.expired ? 'text-danger' : timeLeft.hours < 2 ? 'text-warning' : 'text-foreground'}`}>
      <Icon name="ClockIcon" size={15} className="flex-shrink-0" />
      {timeLeft.expired ? (
        <span className="text-sm font-600">Window expired</span>
      ) : (
        <div className="flex items-center gap-1 font-mono text-sm font-700">
          <span className="px-1.5 py-0.5 bg-muted rounded">{pad(timeLeft.hours)}</span>
          <span>:</span>
          <span className="px-1.5 py-0.5 bg-muted rounded">{pad(timeLeft.minutes)}</span>
          <span>:</span>
          <span className="px-1.5 py-0.5 bg-muted rounded">{pad(timeLeft.seconds)}</span>
        </div>
      )}
    </div>
  );
}

function QRCodeDisplay({ value }: { value: string }) {
  // Simple visual QR representation using the code value
  const rows = 7;
  const cols = 7;
  // Generate a deterministic pattern from the value string
  const pattern = Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (_, c) => {
      const idx = (r * cols + c) % value.length;
      return value.charCodeAt(idx) % 2 === 0;
    })
  );

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="p-3 bg-white rounded-xl border border-border shadow-sm inline-block">
        <div className="grid gap-0.5" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
          {pattern.map((row, r) =>
            row.map((filled, c) => (
              <div
                key={`qr-${r}-${c}`}
                className={`w-5 h-5 rounded-sm ${filled ? 'bg-foreground' : 'bg-white'}`}
              />
            ))
          )}
        </div>
      </div>
      <p className="text-xs font-mono text-muted-foreground tracking-wider">{value}</p>
    </div>
  );
}

export default function AcceptedBidDetailsContent() {
  const { user } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const deliveryId = searchParams.get('id');
  const supabase = createClient();

  const [delivery, setDelivery] = useState<DeliveryDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmReadyOpen, setConfirmReadyOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [confirmReceiptOpen, setConfirmReceiptOpen] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [podUploading, setPodUploading] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [arrivedOpen, setArrivedOpen] = useState(false);
  const [approveFundsOpen, setApproveFundsOpen] = useState(false);
  const podFileRef = useRef<HTMLInputElement>(null);
  const podCameraRef = useRef<HTMLInputElement>(null);

  const fetchDelivery = useCallback(async () => {
    if (!user || !deliveryId) return;
    try {
      const { data, error } = await supabase
        .from('deliveries')
        .select('*')
        .eq('id', deliveryId)
        .single();

      if (error) throw error;

      const d = data;
      setDelivery({
        id: d.id,
        orderId: d.order_id || d.id,
        route: d.route,
        pickupCity: d.pickup_city,
        destinationCity: d.destination_city,
        parcelCategory: d.parcel_category,
        parcelDescription: d.parcel_description,
        weightKg: parseFloat(d.weight_kg) || 0,
        status: d.status,
        travelerName: d.traveler_name,
        travelerUsername: d.traveler_username,
        travelerRating: parseFloat(d.traveler_rating) || 5,
        travelerPhone: d.traveler_phone,
        travelerVerified: d.traveler_verified,
        travelerId: d.traveler_id || '',
        senderId: d.sender_id || '',
        guarantorName: d.guarantor_name,
        guarantorPhone: d.guarantor_phone,
        transportMethod: d.transport_method,
        departureTime: d.departure_time,
        estimatedArrival: d.estimated_arrival,
        offeredPriceKes: parseFloat(d.offered_price_kes) || 0,
        deliveryPin: d.delivery_pin,
        qrCode: d.qr_code,
        matchedAt: d.matched_at,
        senderReadyConfirmedAt: d.sender_ready_confirmed_at,
        senderCancelledAt: d.sender_cancelled_at,
        travelerPodUploadedAt: d.traveler_pod_uploaded_at,
        senderReceiptConfirmedAt: d.sender_receipt_confirmed_at,
        escrowReleasedAt: d.escrow_released_at,
        podPhotoUrl: d.pod_photo_url || '',
        deliveryPhotoUrl: d.delivery_photo_url || '',
        arrivedAtDestinationAt: d.arrived_at_destination_at || null,
      });
    } catch (err) {
      console.error('Error fetching delivery:', err);
      toast.error('Could not load delivery details.');
    } finally {
      setLoading(false);
    }
  }, [user, deliveryId]);

  useEffect(() => {
    if (!deliveryId) {
      router.push('/active-deliveries');
      return;
    }
    fetchDelivery();

    const channel = supabase
      .channel(`delivery_detail_${deliveryId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'deliveries',
        filter: `id=eq.${deliveryId}`,
      }, () => {
        fetchDelivery();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [deliveryId, fetchDelivery]);

  const isSender = delivery && user?.id === delivery.senderId;
  const isTraveler = delivery && user?.id === delivery.travelerId;

  // Pickup window: 4 hours after departure_time
  const pickupWindowEnd = delivery?.departureTime
    ? new Date(new Date(delivery.departureTime).getTime() + 4 * 3600000).toISOString()
    : null;

  // Delivery window: 4 hours after estimated_arrival
  const deliveryWindowEnd = delivery?.estimatedArrival
    ? new Date(new Date(delivery.estimatedArrival).getTime() + 4 * 3600000).toISOString()
    : null;

  const handleConfirmReady = async () => {
    if (!delivery) return;
    setProcessing(true);
    try {
      const now = new Date().toISOString();
      await supabase.from('deliveries').update({
        sender_ready_confirmed_at: now,
        status: 'payment_escrowed',
        payment_escrowed_at: now,
        updated_at: now,
      }).eq('id', delivery.id);

      await supabase.from('tracking_events').insert({
        delivery_id: delivery.id,
        event_type: 'payment_escrowed',
        title: 'Payment Escrowed — Parcel Ready',
        description: 'Sender confirmed parcel is ready for pickup. Payment has been held in escrow. Travel chat is now open.',
        location_name: delivery.pickupCity,
        recorded_by: user?.id,
      });

      await supabase.from('notifications').insert({
        user_id: delivery.travelerId,
        type: 'sender_ready',
        title: '📦 Parcel Ready — Payment Escrowed',
        message: `${delivery.pickupCity} → ${delivery.destinationCity}: The sender has confirmed the parcel is ready and payment is in escrow. Proceed to pickup location. Travel chat is now open.`,
        is_read: false,
      });

      toast.success('Confirmed! Payment escrowed. The traveler has been notified to pick up the parcel.');
      setConfirmReadyOpen(false);
      fetchDelivery();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to confirm. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    if (!delivery) return;
    setProcessing(true);
    try {
      const now = new Date().toISOString();
      await supabase.from('deliveries').update({
        sender_cancelled_at: now,
        updated_at: now,
      }).eq('id', delivery.id);

      await supabase.from('tracking_events').insert({
        delivery_id: delivery.id,
        event_type: 'cancelled',
        title: 'Delivery Cancelled by Sender',
        description: cancelReason ? `Reason: ${cancelReason}` : 'Sender cancelled the delivery.',
        location_name: delivery.pickupCity,
        recorded_by: user?.id,
      });

      await supabase.from('notifications').insert({
        user_id: delivery.travelerId,
        type: 'delivery_cancelled',
        title: '❌ Delivery Cancelled',
        message: `The sender has cancelled the delivery for ${delivery.pickupCity} → ${delivery.destinationCity}.${cancelReason ? ` Reason: ${cancelReason}` : ''}`,
        is_read: false,
      });

      toast.success('Delivery cancelled. The traveler has been notified.');
      setCancelOpen(false);
      router.push('/active-deliveries');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to cancel. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkArrived = async () => {
    if (!delivery) return;
    setProcessing(true);
    try {
      const now = new Date().toISOString();

      // Only mark arrived + set status to delivered — do NOT set traveler_pod_uploaded_at here
      await supabase.from('deliveries').update({
        arrived_at_destination_at: now,
        status: 'delivered',
        delivered_at: now,
        updated_at: now,
      }).eq('id', delivery.id);

      await supabase.from('tracking_events').insert({
        delivery_id: delivery.id,
        event_type: 'delivered',
        title: 'Traveler Arrived at Destination',
        description: `Traveler confirmed arrival at ${delivery.destinationCity}. Awaiting customer approval to release funds.`,
        location_name: delivery.destinationCity,
        recorded_by: user?.id,
      });

      await supabase.from('notifications').insert([
        {
          user_id: delivery.senderId,
          type: 'traveler_arrived',
          title: '📍 Traveler Has Arrived',
          message: `Your traveler has arrived at ${delivery.destinationCity} with your parcel (${delivery.route}). Please confirm with the recipient and guarantor that delivery is complete, then approve the fund release.`,
          is_read: false,
        },
      ]);

      toast.success('Arrival confirmed! The customer has been notified to approve fund release.');
      setArrivedOpen(false);
      fetchDelivery();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to mark arrival. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveFundRelease = async () => {
    if (!delivery) return;
    setProcessing(true);
    try {
      const now = new Date().toISOString();
      const mpesaRef = `MPE${Date.now().toString().slice(-8)}`;

      await supabase.from('deliveries').update({
        sender_receipt_confirmed_at: now,
        escrow_released_at: now,
        escrow_release_mpesa_ref: mpesaRef,
        status: 'completed',
        completed_at: now,
        updated_at: now,
      }).eq('id', delivery.id);

      // Update order status to completed
      if (delivery.orderId && delivery.orderId !== delivery.id) {
        await supabase
          .from('orders')
          .update({ status: 'completed', updated_at: now })
          .eq('id', delivery.orderId);
      }

      await supabase.from('tracking_events').insert({
        delivery_id: delivery.id,
        event_type: 'completed',
        title: 'Customer Approved — Escrow Released',
        description: `Customer approved fund release after confirming recipient and guarantor agreement. KES ${delivery.offeredPriceKes.toLocaleString()} released to traveler's M-Pesa (Ref: ${mpesaRef}).`,
        location_name: delivery.destinationCity,
        recorded_by: user?.id,
      });

      await supabase.from('transactions').insert({
        order_id: delivery.orderId,
        user_id: delivery.travelerId,
        route: delivery.route,
        amount: delivery.offeredPriceKes,
        method: 'mpesa',
        mpesa_ref: mpesaRef,
        status: 'released',
        type: 'release',
        traveler_name: delivery.travelerName,
        parcel_category: delivery.parcelCategory,
        service_fee: Math.round(delivery.offeredPriceKes * 0.05),
        traveler_earns: Math.round(delivery.offeredPriceKes * 0.95),
        released_at: now,
      });

      const isValidOrderId = delivery.orderId && delivery.orderId !== delivery.id;
      if (isValidOrderId) {
        await supabase.from('traveler_earnings').insert({
          traveler_id: delivery.travelerId,
          order_id: delivery.orderId,
          gross_amount: delivery.offeredPriceKes,
          platform_fee: Math.round(delivery.offeredPriceKes * 0.05),
          net_amount: Math.round(delivery.offeredPriceKes * 0.95),
          route: delivery.route,
          parcel_category: delivery.parcelCategory,
          status: 'settled',
          settled_at: now,
        });
      } else {
        await supabase.from('traveler_earnings').insert({
          traveler_id: delivery.travelerId,
          gross_amount: delivery.offeredPriceKes,
          platform_fee: Math.round(delivery.offeredPriceKes * 0.05),
          net_amount: Math.round(delivery.offeredPriceKes * 0.95),
          route: delivery.route,
          parcel_category: delivery.parcelCategory,
          status: 'settled',
          settled_at: now,
        });
      }

      await supabase.from('notifications').insert([
        {
          user_id: delivery.travelerId,
          type: 'escrow_released',
          title: '💰 Payment Released to M-Pesa',
          message: `KES ${Math.round(delivery.offeredPriceKes * 0.95).toLocaleString()} has been released to your M-Pesa (Ref: ${mpesaRef}). Delivery complete!`,
          is_read: false,
        },
        {
          user_id: delivery.senderId,
          type: 'delivery_complete',
          title: '✅ Delivery Complete',
          message: `Your parcel (${delivery.route}) has been delivered and payment released. Thank you for using MuyuRide!`,
          is_read: false,
        },
        {
          user_id: delivery.travelerId,
          type: 'review_pending',
          title: '⭐ Leave a Review',
          message: `How was your delivery experience? Rate the sender for the ${delivery.route} delivery. Reviews help build trust in our community.`,
          is_read: false,
        },
        {
          user_id: delivery.senderId,
          type: 'review_pending',
          title: '⭐ Rate Your Traveler',
          message: `How was ${delivery.travelerName}? Leave a review for your ${delivery.route} delivery. Your feedback helps other senders.`,
          is_read: false,
        },
      ]);

      sendEscrowReleasedEmail({
        senderId: delivery.senderId,
        senderName: 'Sender',
        travelerId: delivery.travelerId,
        travelerName: delivery.travelerName,
        route: delivery.route,
        pickupCity: delivery.pickupCity,
        destinationCity: delivery.destinationCity,
        parcelCategory: delivery.parcelCategory,
        weightKg: delivery.weightKg,
        offeredPriceKes: delivery.offeredPriceKes,
        mpesaRef,
        deliveryId: delivery.id,
      }).catch(() => {});

      toast.success(`Funds approved! KES ${Math.round(delivery.offeredPriceKes * 0.95).toLocaleString()} released to traveler's M-Pesa.`);
      setApproveFundsOpen(false);
      fetchDelivery();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to approve fund release. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handlePodUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!delivery || !isTraveler) return;
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_SIZE = 50 * 1024 * 1024; // 50 MB
    if (file.size > MAX_SIZE) {
      toast.error('File too large. Maximum size is 50 MB.');
      return;
    }

    // Validate it's an image
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are accepted for proof of delivery.');
      return;
    }

    setPodUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      // Use a unique path every time (no upsert) so only INSERT permission is needed
      const path = `${delivery.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('proof-of-delivery')
        .upload(path, file, { upsert: false, contentType: file.type });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('proof-of-delivery')
        .getPublicUrl(path);

      const photoUrl = urlData.publicUrl;
      const now = new Date().toISOString();

      const { error: updateError } = await supabase.from('deliveries').update({
        pod_photo_url: photoUrl,
        delivery_photo_url: photoUrl,
        traveler_pod_uploaded_at: now,
        status: 'delivered',
        delivered_at: now,
        updated_at: now,
      }).eq('id', delivery.id);

      if (updateError) throw updateError;

      await supabase.from('tracking_events').insert({
        delivery_id: delivery.id,
        event_type: 'delivered',
        title: 'Proof of Delivery Uploaded',
        description: 'Traveler uploaded proof-of-delivery photo. Awaiting sender confirmation.',
        location_name: delivery.destinationCity,
        photo_urls: [photoUrl],
        recorded_by: user?.id,
      });

      await supabase.from('notifications').insert({
        user_id: delivery.senderId,
        type: 'pod_uploaded',
        title: '📸 Proof of Delivery Uploaded',
        message: `Your parcel (${delivery.pickupCity} → ${delivery.destinationCity}) has been delivered. Please confirm receipt to release payment to the traveler.`,
        is_read: false,
      });

      // Send delivery confirmed email (fire-and-forget)
      sendDeliveryConfirmedEmail({
        senderId: delivery.senderId,
        senderName: 'Sender',
        travelerId: delivery.travelerId,
        travelerName: delivery.travelerName,
        route: delivery.route,
        pickupCity: delivery.pickupCity,
        destinationCity: delivery.destinationCity,
        parcelCategory: delivery.parcelCategory,
        weightKg: delivery.weightKg,
        offeredPriceKes: delivery.offeredPriceKes,
        deliveryId: delivery.id,
      }).catch(() => {});

      toast.success('Proof of delivery uploaded! Waiting for sender to confirm receipt.');
      fetchDelivery();
    } catch (err: any) {
      console.error('POD upload error:', err);
      const msg = err?.message || 'Upload failed. Please try again.';
      if (msg.includes('row-level security') || msg.includes('permission') || msg.includes('policy')) {
        toast.error('Permission error uploading photo. Please refresh and try again.');
      } else if (msg.includes('Payload too large') || msg.includes('size')) {
        toast.error('Photo is too large. Please use a photo under 50 MB.');
      } else {
        toast.error(msg);
      }
    } finally {
      setPodUploading(false);
      if (podFileRef.current) podFileRef.current.value = '';
      if (podCameraRef.current) podCameraRef.current.value = '';
    }
  };

  const handleConfirmReceipt = async () => {
    if (!delivery) return;
    if (pinInput !== delivery.deliveryPin) {
      setPinError('Incorrect PIN. Please ask the traveler for the 4-digit delivery PIN.');
      return;
    }
    setProcessing(true);
    try {
      const now = new Date().toISOString();
      const mpesaRef = `MPE${Date.now().toString().slice(-8)}`;

      await supabase.from('deliveries').update({
        sender_receipt_confirmed_at: now,
        escrow_released_at: now,
        escrow_release_mpesa_ref: mpesaRef,
        status: 'completed',
        completed_at: now,
        updated_at: now,
      }).eq('id', delivery.id);

      // Update order status to completed
      if (delivery.orderId && delivery.orderId !== delivery.id) {
        await supabase
          .from('orders')
          .update({ status: 'completed', updated_at: now })
          .eq('id', delivery.orderId);
      }

      await supabase.from('tracking_events').insert({
        delivery_id: delivery.id,
        event_type: 'completed',
        title: 'Receipt Confirmed — Escrow Released',
        description: `Sender confirmed receipt with PIN. Escrow payment of KES ${delivery.offeredPriceKes.toLocaleString()} released to traveler's M-Pesa.`,
        location_name: delivery.destinationCity,
        pin_verified: true,
        recorded_by: user?.id,
      });

      // Release escrow: create a release transaction record (order_id is TEXT in transactions)
      await supabase.from('transactions').insert({
        order_id: delivery.orderId,
        user_id: delivery.travelerId,
        route: delivery.route,
        amount: delivery.offeredPriceKes,
        method: 'mpesa',
        mpesa_ref: mpesaRef,
        status: 'released',
        type: 'release',
        traveler_name: delivery.travelerName,
        parcel_category: delivery.parcelCategory,
        service_fee: Math.round(delivery.offeredPriceKes * 0.05),
        traveler_earns: Math.round(delivery.offeredPriceKes * 0.95),
        released_at: now,
      });

      // Traveler earnings record — only insert if orderId is a valid UUID (i.e. from orders table)
      // traveler_earnings.order_id is a UUID FK; skip if orderId equals delivery.id (fallback)
      const isValidOrderId = delivery.orderId && delivery.orderId !== delivery.id;
      if (isValidOrderId) {
        await supabase.from('traveler_earnings').insert({
          traveler_id: delivery.travelerId,
          order_id: delivery.orderId,
          gross_amount: delivery.offeredPriceKes,
          platform_fee: Math.round(delivery.offeredPriceKes * 0.05),
          net_amount: Math.round(delivery.offeredPriceKes * 0.95),
          route: delivery.route,
          parcel_category: delivery.parcelCategory,
          status: 'settled',
          settled_at: now,
        });
      } else {
        // Insert without order_id FK to avoid constraint violation
        await supabase.from('traveler_earnings').insert({
          traveler_id: delivery.travelerId,
          gross_amount: delivery.offeredPriceKes,
          platform_fee: Math.round(delivery.offeredPriceKes * 0.05),
          net_amount: Math.round(delivery.offeredPriceKes * 0.95),
          route: delivery.route,
          parcel_category: delivery.parcelCategory,
          status: 'settled',
          settled_at: now,
        });
      }

      await supabase.from('notifications').insert([
        {
          user_id: delivery.travelerId,
          type: 'escrow_released',
          title: '💰 Payment Released to M-Pesa',
          message: `KES ${Math.round(delivery.offeredPriceKes * 0.95).toLocaleString()} has been released to your M-Pesa (Ref: ${mpesaRef}). Delivery complete!`,
          is_read: false,
        },
        {
          user_id: delivery.senderId,
          type: 'delivery_complete',
          title: '✅ Delivery Complete',
          message: `Your parcel (${delivery.route}) has been delivered and payment released. Thank you for using MuyuRide!`,
          is_read: false,
        },
        {
          user_id: delivery.travelerId,
          type: 'review_pending',
          title: '⭐ Leave a Review',
          message: `How was your delivery experience? Rate the sender for the ${delivery.route} delivery. Reviews help build trust in our community.`,
          is_read: false,
        },
        {
          user_id: delivery.senderId,
          type: 'review_pending',
          title: '⭐ Rate Your Traveler',
          message: `How was ${delivery.travelerName}? Leave a review for your ${delivery.route} delivery. Your feedback helps other senders.`,
          is_read: false,
        },
      ]);

      // Send escrow released email (fire-and-forget)
      sendEscrowReleasedEmail({
        senderId: delivery.senderId,
        senderName: 'Sender',
        travelerId: delivery.travelerId,
        travelerName: delivery.travelerName,
        route: delivery.route,
        pickupCity: delivery.pickupCity,
        destinationCity: delivery.destinationCity,
        parcelCategory: delivery.parcelCategory,
        weightKg: delivery.weightKg,
        offeredPriceKes: delivery.offeredPriceKes,
        mpesaRef,
        deliveryId: delivery.id,
      }).catch(() => {});

      toast.success(`Delivery complete! KES ${Math.round(delivery.offeredPriceKes * 0.95).toLocaleString()} released to traveler's M-Pesa.`);
      setConfirmReceiptOpen(false);
      fetchDelivery();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to confirm receipt. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Icon name="ArrowPathIcon" size={32} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!delivery) {
    return (
      <div className="flex flex-col items-center justify-center py-24 space-y-3">
        <Icon name="ExclamationCircleIcon" size={40} className="text-muted-foreground" />
        <p className="text-base font-600 text-foreground">Delivery not found</p>
        <Link href="/active-deliveries" className="text-sm text-primary hover:underline">
          Back to Active Deliveries
        </Link>
      </div>
    );
  }

  const statusStep = statusConfig[delivery.status].step;
  const isCancelled = !!delivery.senderCancelledAt;
  const isCompleted = delivery.status === 'completed';

  const steps = [
    { step: 1, label: 'Matched', icon: 'CheckBadgeIcon' },
    { step: 2, label: 'Escrowed', icon: 'LockClosedIcon' },
    { step: 3, label: 'Picked Up', icon: 'ArchiveBoxIcon' },
    { step: 4, label: 'In Transit', icon: 'TruckIcon' },
    { step: 5, label: 'Delivered', icon: 'MapPinIcon' },
    { step: 6, label: 'Complete', icon: 'CheckCircleIcon' },
  ];

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div>
        <button
          onClick={() => router.push('/active-deliveries')}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3 min-h-[44px]"
        >
          <Icon name="ArrowLeftIcon" size={16} />
          Active Deliveries
        </button>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl sm:text-2xl font-700 text-foreground">Accepted Bid Details</h1>
            <p className="text-muted-foreground text-sm mt-1">
              {delivery.pickupCity} → {delivery.destinationCity} · {delivery.parcelCategory}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge
              label={isCancelled ? 'Cancelled' : statusConfig[delivery.status].label}
              variant={isCancelled ? 'danger' : statusConfig[delivery.status].variant}
              dot
            />
            {isCompleted && (
              <span className="flex items-center gap-1 px-2.5 py-1 bg-success/10 border border-success/20 rounded-full text-xs font-600 text-success">
                <Icon name="BanknotesIcon" size={12} />
                Escrow Released
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="card-elevated p-4 sm:p-5">
        <div className="flex items-center justify-between relative">
          <div className="absolute left-0 right-0 top-4 h-0.5 bg-border mx-4 sm:mx-8" />
          <div
            className="absolute left-0 top-4 h-0.5 bg-primary transition-all duration-700 mx-4 sm:mx-8"
            style={{ width: `${Math.max(0, ((statusStep - 1) / (steps.length - 1)) * 100)}%` }}
          />
          {steps.map(s => (
            <div key={s.step} className="flex flex-col items-center gap-1 sm:gap-1.5 relative z-10">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                s.step < statusStep ? 'bg-primary border-primary' :
                s.step === statusStep ? 'bg-primary border-primary ring-4 ring-primary/20': 'bg-card border-border'
              }`}>
                <Icon
                  name={s.icon as Parameters<typeof Icon>[0]['name']}
                  size={14}
                  className={s.step <= statusStep ? 'text-primary-foreground' : 'text-muted-foreground'}
                />
              </div>
              <span className={`text-[9px] sm:text-[10px] font-600 text-center leading-tight max-w-[40px] sm:max-w-none ${s.step <= statusStep ? 'text-foreground' : 'text-muted-foreground'}`}>
                {s.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Traveler Info */}
      <div className="card-elevated p-5 space-y-4">
        <h2 className="text-sm font-700 text-foreground flex items-center gap-2">
          <Icon name="UserCircleIcon" size={16} className="text-primary" />
          Traveler Information
        </h2>
        <div className="flex items-start gap-4">
          <div className="relative flex-shrink-0">
            <div className="w-14 h-14 rounded-full gradient-primary flex items-center justify-center text-primary-foreground font-700 text-xl">
              {delivery.travelerName.charAt(0).toUpperCase()}
            </div>
            {delivery.travelerVerified && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-success flex items-center justify-center border-2 border-card">
                <Icon name="CheckBadgeIcon" size={10} className="text-white" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-700 text-foreground">{delivery.travelerName}</span>
              {delivery.travelerVerified && (
                <span className="text-[10px] font-600 text-primary bg-primary/10 border border-primary/20 px-1.5 py-0.5 rounded-full">
                  Approved Traveler
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">@{delivery.travelerUsername}</p>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {delivery.travelerRating > 0 && (
                <span className="flex items-center gap-1 text-xs">
                  <Icon name="StarIcon" size={12} className="text-warning" />
                  <span className="font-600 text-foreground">{delivery.travelerRating.toFixed(1)}</span>
                  <span className="text-muted-foreground">rating</span>
                </span>
              )}
              {delivery.travelerPhone && (
                <a
                  href={`tel:${delivery.travelerPhone}`}
                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <Icon name="PhoneIcon" size={12} />
                  {delivery.travelerPhone}
                </a>
              )}
              <Link
                href={`/public-profile?id=${delivery.travelerId}`}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="ArrowTopRightOnSquareIcon" size={12} />
                View Profile
              </Link>
            </div>
          </div>
        </div>

        {/* Guarantor */}
        {delivery.guarantorName && (
          <div className="flex items-center gap-3 p-3 rounded-xl bg-success/5 border border-success/15">
            <Icon name="ShieldCheckIcon" size={16} className="text-success flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-600 text-foreground">Guarantor: {delivery.guarantorName}</p>
              {delivery.guarantorPhone && (
                <p className="text-xs text-muted-foreground">{delivery.guarantorPhone}</p>
              )}
            </div>
            <span className="text-[10px] font-600 text-success bg-success/10 border border-success/20 px-1.5 py-0.5 rounded-full">Verified</span>
          </div>
        )}
      </div>

      {/* Trip Route & Windows */}
      <div className="card-elevated p-5 space-y-4">
        <h2 className="text-sm font-700 text-foreground flex items-center gap-2">
          <Icon name="MapIcon" size={16} className="text-primary" />
          Trip Route & Schedule
        </h2>

        {/* Route visual */}
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-primary" />
            <div className="w-px h-8 bg-border" />
            <div className="w-3 h-3 rounded-full bg-success" />
          </div>
          <div className="flex-1 space-y-3">
            <div>
              <p className="text-xs text-muted-foreground">Pickup</p>
              <p className="text-sm font-600 text-foreground">{delivery.pickupCity}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Destination</p>
              <p className="text-sm font-600 text-foreground">{delivery.destinationCity}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <Icon name="TruckIcon" size={12} className="text-primary" />
              {delivery.transportMethod}
            </div>
            <p className="text-xs font-700 text-foreground">KES {delivery.offeredPriceKes.toLocaleString()}</p>
          </div>
        </div>

        {/* Time windows */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="p-3 rounded-xl bg-muted/50 space-y-2">
            <p className="text-xs font-600 text-foreground flex items-center gap-1.5">
              <Icon name="ArrowUpTrayIcon" size={13} className="text-primary" />
              Pickup Window
            </p>
            {delivery.departureTime ? (
              <>
                <p className="text-xs text-muted-foreground">
                  {new Date(delivery.departureTime).toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' })}
                </p>
                <CountdownTimer targetDate={pickupWindowEnd} />
              </>
            ) : (
              <p className="text-xs text-muted-foreground">Not set</p>
            )}
          </div>
          <div className="p-3 rounded-xl bg-muted/50 space-y-2">
            <p className="text-xs font-600 text-foreground flex items-center gap-1.5">
              <Icon name="ArrowDownTrayIcon" size={13} className="text-success" />
              Delivery Window
            </p>
            {delivery.estimatedArrival ? (
              <>
                <p className="text-xs text-muted-foreground">
                  {new Date(delivery.estimatedArrival).toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' })}
                </p>
                <CountdownTimer targetDate={deliveryWindowEnd} />
              </>
            ) : (
              <p className="text-xs text-muted-foreground">Not set</p>
            )}
          </div>
        </div>
      </div>

      {/* Delivery PIN & QR Code */}
      <div className="card-elevated p-5 space-y-5">
        <h2 className="text-sm font-700 text-foreground flex items-center gap-2">
          <Icon name="QrCodeIcon" size={16} className="text-primary" />
          Delivery Credentials
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* PIN */}
          <div className="flex flex-col items-center gap-3">
            <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider">Delivery PIN</p>
            <div className="flex gap-2">
              {delivery.deliveryPin.split('').map((digit, i) => (
                <div
                  key={`pin-${i}`}
                  className="w-12 h-14 rounded-xl border-2 border-primary/30 bg-primary/5 flex items-center justify-center text-2xl font-700 text-primary"
                >
                  {isSender ? digit : '•'}
                </div>
              ))}
            </div>
            {isSender && (
              <p className="text-xs text-muted-foreground text-center">
                Share this PIN with the traveler at pickup. They'll need it to confirm delivery.
              </p>
            )}
            {isTraveler && (
              <p className="text-xs text-muted-foreground text-center">
                Ask the sender for the 4-digit PIN to confirm delivery.
              </p>
            )}
          </div>

          {/* QR Code */}
          <div className="flex flex-col items-center gap-3">
            <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider">QR Code</p>
            <QRCodeDisplay value={delivery.qrCode} />
            <p className="text-xs text-muted-foreground text-center">
              Scan at pickup station for verification
            </p>
          </div>
        </div>
      </div>

      {/* Parcel Details */}
      <div className="card-elevated p-5 space-y-3">
        <h2 className="text-sm font-700 text-foreground flex items-center gap-2">
          <Icon name="ArchiveBoxIcon" size={16} className="text-primary" />
          Parcel Details
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            { label: 'Category', value: delivery.parcelCategory },
            { label: 'Weight', value: `${delivery.weightKg} kg` },
            { label: 'Price', value: `KES ${delivery.offeredPriceKes.toLocaleString()}` },
          ].map(item => (
            <div key={item.label} className="p-3 rounded-xl bg-muted/50">
              <p className="text-[10px] text-muted-foreground">{item.label}</p>
              <p className="text-sm font-600 text-foreground mt-0.5">{item.value}</p>
            </div>
          ))}
        </div>
        {delivery.parcelDescription && (
          <p className="text-xs text-muted-foreground">{delivery.parcelDescription}</p>
        )}
      </div>

      {/* Active Delivery: Proof of Delivery Section */}
      {(delivery.status === 'pickup_confirmed' || delivery.status === 'in_transit' || delivery.status === 'delivered' || delivery.status === 'completed') && (
        <div className="card-elevated p-5 space-y-4">
          <h2 className="text-sm font-700 text-foreground flex items-center gap-2">
            <Icon name="CameraIcon" size={16} className="text-primary" />
            Proof of Delivery
          </h2>

          {/* Traveler: Upload POD — show when pickup_confirmed, in_transit, or delivered and not yet uploaded */}
          {isTraveler && (delivery.status === 'pickup_confirmed' || delivery.status === 'in_transit' || delivery.status === 'delivered') && !delivery.travelerPodUploadedAt && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Take a photo or select one from your gallery to upload proof of delivery and trigger payment release.
              </p>

              {/* Hidden file inputs */}
              <input
                ref={podCameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePodUpload}
                className="hidden"
                id="pod-camera"
              />
              <input
                ref={podFileRef}
                type="file"
                accept="image/*"
                onChange={handlePodUpload}
                className="hidden"
                id="pod-upload"
              />

              {podUploading ? (
                <div className="flex items-center justify-center gap-2 w-full py-4 rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 text-sm font-600 text-primary">
                  <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                  Uploading photo…
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <label
                    htmlFor="pod-camera"
                    className="flex flex-col items-center justify-center gap-2 py-4 rounded-xl border-2 border-dashed border-primary/40 text-sm font-600 text-primary cursor-pointer hover:bg-primary/5 transition-colors"
                  >
                    <Icon name="CameraIcon" size={20} />
                    <span className="text-xs text-center leading-tight">Take Photo</span>
                  </label>
                  <label
                    htmlFor="pod-upload"
                    className="flex flex-col items-center justify-center gap-2 py-4 rounded-xl border-2 border-dashed border-border text-sm font-600 text-muted-foreground cursor-pointer hover:bg-muted/50 transition-colors"
                  >
                    <Icon name="PhotoIcon" size={20} />
                    <span className="text-xs text-center leading-tight">Choose from Gallery</span>
                  </label>
                </div>
              )}
            </div>
          )}

          {/* POD uploaded — show photo */}
          {(delivery.podPhotoUrl || delivery.deliveryPhotoUrl) && (
            <div className="space-y-2">
              <p className="text-xs font-600 text-success flex items-center gap-1.5">
                <Icon name="CheckCircleIcon" size={13} />
                Proof of delivery uploaded
                {delivery.travelerPodUploadedAt && (
                  <span className="text-muted-foreground font-400">
                    · {new Date(delivery.travelerPodUploadedAt).toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' })}
                  </span>
                )}
              </p>
              <div className="relative rounded-xl overflow-hidden border border-border">
                <img
                  src={delivery.podPhotoUrl || delivery.deliveryPhotoUrl}
                  alt="Proof of delivery photo showing parcel at destination"
                  className="w-full max-h-64 object-cover"
                />
                <a
                  href={delivery.podPhotoUrl || delivery.deliveryPhotoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 bg-black/60 text-white text-xs font-600 rounded-lg hover:bg-black/80 transition-colors"
                >
                  <Icon name="ArrowDownTrayIcon" size={12} />
                  Download
                </a>
              </div>
            </div>
          )}

      {/* Sender: Confirm Receipt */}
          {isSender && delivery.status === 'delivered' && !delivery.senderReceiptConfirmedAt && (
            <div className="p-4 rounded-xl bg-warning/5 border border-warning/20 space-y-3">
              <div className="flex items-start gap-2">
                <Icon name="ExclamationTriangleIcon" size={16} className="text-warning flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-600 text-foreground">Confirm Receipt to Release Payment</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    The traveler has uploaded proof of delivery. Confirm receipt to release KES {Math.round(delivery.offeredPriceKes * 0.95).toLocaleString()} to their M-Pesa.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setConfirmReceiptOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-success text-white rounded-xl text-sm font-600 hover:opacity-90 transition-opacity"
              >
                <Icon name="CheckCircleIcon" size={15} />
                Confirm Receipt & Release Payment
              </button>
            </div>
          )}

          {/* Escrow released confirmation */}
          {delivery.escrowReleasedAt && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-success/8 border border-success/20">
              <Icon name="BanknotesIcon" size={18} className="text-success flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-600 text-success">Payment Released to Traveler's M-Pesa</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  KES {Math.round(delivery.offeredPriceKes * 0.95).toLocaleString()} released on{' '}
                  {new Date(delivery.escrowReleasedAt).toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' })}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sender Actions (matched / payment_escrowed status) */}
      {isSender && !isCancelled && !isCompleted && (delivery.status === 'matched' || delivery.status === 'payment_escrowed') && (
        <div className="card-elevated p-5 space-y-3">
          <h2 className="text-sm font-700 text-foreground">Sender Actions</h2>

          {delivery.status === 'matched' && !delivery.senderReadyConfirmedAt && (
            <div className="p-4 rounded-xl bg-primary/5 border border-primary/15 space-y-3">
              <p className="text-sm text-muted-foreground">
                Confirm the parcel is ready for pickup. This will escrow the payment and notify the traveler to proceed.
              </p>
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={() => setConfirmReadyOpen(true)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-600 hover:opacity-90 transition-opacity"
                >
                  <Icon name="CheckCircleIcon" size={15} />
                  Confirm Parcel Ready
                </button>
                <button
                  onClick={() => setCancelOpen(true)}
                  className="flex items-center gap-2 px-4 py-2.5 border border-danger/30 text-danger rounded-xl text-sm font-600 hover:bg-danger/5 transition-colors"
                >
                  <Icon name="XCircleIcon" size={15} />
                  Cancel Delivery
                </button>
              </div>
            </div>
          )}

          {delivery.senderReadyConfirmedAt && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 p-3 rounded-xl bg-success/8 border border-success/20">
                <Icon name="CheckCircleIcon" size={15} className="text-success" />
                <p className="text-xs font-600 text-success">
                  Parcel confirmed ready · Payment escrowed ·{' '}
                  {new Date(delivery.senderReadyConfirmedAt).toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' })}
                </p>
              </div>
              {/* Step 6: Travel Chat now open */}
              <div className="flex items-start gap-3 p-3 rounded-xl bg-primary/5 border border-primary/15">
                <Icon name="ChatBubbleLeftRightIcon" size={15} className="text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs font-600 text-foreground">Travel Chat is now open</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Coordinate pickup details with the traveler.</p>
                </div>
                <Link
                  href={`/delivery-messaging?deliveryId=${delivery.id}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-600 hover:bg-primary/90 transition-colors flex-shrink-0"
                >
                  <Icon name="ChatBubbleLeftRightIcon" size={12} />
                  Open Chat
                </Link>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sender: Approve Fund Release — shown when traveler has arrived but escrow not yet released */}
      {isSender && !isCancelled && !isCompleted && delivery.arrivedAtDestinationAt && !delivery.escrowReleasedAt && (
        <div className="card-elevated p-5 space-y-3">
          <h2 className="text-sm font-700 text-foreground flex items-center gap-2">
            <Icon name="BanknotesIcon" size={16} className="text-success" />
            Approve Fund Release
          </h2>
          <div className="p-4 rounded-xl bg-success/5 border border-success/20 space-y-3">
            <div className="flex items-start gap-2">
              <Icon name="MapPinIcon" size={16} className="text-success flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-600 text-foreground">
                  The traveler has arrived at {delivery.destinationCity}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Arrived {new Date(delivery.arrivedAtDestinationAt).toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' })}
                </p>
              </div>
            </div>
            <div className="p-3 rounded-xl bg-warning/5 border border-warning/15 space-y-1.5">
              <p className="text-xs font-600 text-foreground flex items-center gap-1.5">
                <Icon name="ExclamationTriangleIcon" size={13} className="text-warning" />
                Before approving, please confirm:
              </p>
              <ul className="text-xs text-muted-foreground space-y-1 pl-4 list-disc">
                <li>The <strong>recipient</strong> has confirmed they received the parcel</li>
                <li>The <strong>guarantor ({delivery.guarantorName || 'guarantor'})</strong> has confirmed the delivery is complete</li>
                <li>You are satisfied the delivery was fulfilled as agreed</li>
              </ul>
            </div>
            <button
              onClick={() => setApproveFundsOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-success text-white rounded-xl text-sm font-600 hover:opacity-90 transition-opacity"
            >
              <Icon name="BanknotesIcon" size={15} />
              Approve & Release KES {Math.round(delivery.offeredPriceKes * 0.95).toLocaleString()} to Traveler
            </button>
          </div>
        </div>
      )}

      {/* Traveler Actions */}
      {isTraveler && !isCancelled && !isCompleted && (
        <div className="card-elevated p-5 space-y-3">
          <h2 className="text-sm font-700 text-foreground">Traveler Actions</h2>

          {/* Travel Chat — available once payment is escrowed */}
          {(delivery.status === 'payment_escrowed' || delivery.status === 'pickup_confirmed' || delivery.status === 'in_transit' || delivery.status === 'delivered') && (
            <div className="flex items-center gap-3 p-3 rounded-xl bg-primary/5 border border-primary/15">
              <Icon name="ChatBubbleLeftRightIcon" size={15} className="text-primary flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs font-600 text-foreground">Travel Chat</p>
                <p className="text-xs text-muted-foreground">Communicate with the sender about pickup and delivery.</p>
              </div>
              <Link
                href={`/delivery-messaging?deliveryId=${delivery.id}`}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-600 hover:bg-primary/90 transition-colors flex-shrink-0"
              >
                <Icon name="ChatBubbleLeftRightIcon" size={12} />
                Open Chat
              </Link>
            </div>
          )}

          {/* Mark Arrived at Destination — show for pickup_confirmed OR in_transit (before arrival confirmed) */}
          {(delivery.status === 'pickup_confirmed' || delivery.status === 'in_transit') && !delivery.arrivedAtDestinationAt && (
            <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 space-y-3">
              <div className="flex items-start gap-2">
                <Icon name="MapPinIcon" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-600 text-foreground">Have you arrived at {delivery.destinationCity}?</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Tap below once you have handed the parcel to the recipient. The customer will then confirm with the recipient and guarantor before approving the fund release to your M-Pesa.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setArrivedOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-600 hover:opacity-90 transition-opacity"
              >
                <Icon name="MapPinIcon" size={15} />
                I Have Arrived
              </button>
            </div>
          )}

          {/* Arrived — waiting for customer approval */}
          {delivery.arrivedAtDestinationAt && !delivery.escrowReleasedAt && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 p-3 rounded-xl bg-success/8 border border-success/20">
                <Icon name="CheckCircleIcon" size={15} className="text-success" />
                <p className="text-xs font-600 text-success">
                  Arrived at {delivery.destinationCity} ·{' '}
                  {new Date(delivery.arrivedAtDestinationAt).toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' })}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-warning/5 border border-warning/15">
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Icon name="ClockIcon" size={13} className="text-warning" />
                  Waiting for the customer to approve fund release after confirming with the recipient and guarantor.
                </p>
              </div>
            </div>
          )}

          {/* Arrived + escrow released */}
          {delivery.arrivedAtDestinationAt && delivery.escrowReleasedAt && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-success/8 border border-success/20">
              <Icon name="CheckCircleIcon" size={15} className="text-success" />
              <p className="text-xs font-600 text-success">
                Arrived at {delivery.destinationCity} ·{' '}
                {new Date(delivery.arrivedAtDestinationAt).toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' })}
              </p>
            </div>
          )}

          {/* In-transit: remind to mark arrived if not yet done */}
          {delivery.status === 'in_transit' && !delivery.arrivedAtDestinationAt && (
            <div className="p-3 rounded-xl bg-primary/5 border border-primary/15">
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Icon name="MapPinIcon" size={13} className="text-primary" />
                Once you hand the parcel to the recipient at {delivery.destinationCity}, tap "I Have Arrived" above to notify the customer.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Cancelled notice */}
      {isCancelled && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-danger/5 border border-danger/20">
          <Icon name="XCircleIcon" size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-600 text-danger">Delivery Cancelled</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Cancelled on {delivery.senderCancelledAt ? new Date(delivery.senderCancelledAt).toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
            </p>
          </div>
        </div>
      )}

      {/* Quick links */}
      <div className="flex gap-3 flex-wrap">
        <Link
          href={`/active-deliveries`}
          className="flex items-center gap-2 px-4 py-2 border border-border rounded-xl text-sm font-600 hover:bg-muted transition-colors"
        >
          <Icon name="TruckIcon" size={14} />
          Active Deliveries
        </Link>
        <Link
          href={`/live-tracking`}
          className="flex items-center gap-2 px-4 py-2 border border-border rounded-xl text-sm font-600 hover:bg-muted transition-colors"
        >
          <Icon name="SignalIcon" size={14} />
          Live Tracking
        </Link>
        <Link
          href={`/delivery-messaging?deliveryId=${delivery.id}`}
          className="flex items-center gap-2 px-4 py-2 border border-border rounded-xl text-sm font-600 hover:bg-muted transition-colors"
        >
          <Icon name="ChatBubbleLeftRightIcon" size={14} />
          Message Traveler
        </Link>
      </div>

      {/* Mark Arrived Modal */}
      <Modal
        isOpen={arrivedOpen}
        onClose={() => setArrivedOpen(false)}
        title="Confirm Arrival at Destination"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 rounded-xl bg-primary/5 border border-primary/15">
            <Icon name="MapPinIcon" size={16} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-600 text-foreground">You have arrived at {delivery.destinationCity}?</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Confirming arrival will notify the customer. Funds will only be released after the customer approves — once the recipient and guarantor confirm the delivery is complete.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2 p-3 rounded-xl bg-warning/5 border border-warning/15">
            <Icon name="ExclamationTriangleIcon" size={14} className="text-warning flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              Only confirm if you have physically handed the parcel to the recipient. This action cannot be undone.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setArrivedOpen(false)}
              disabled={processing}
              className="flex-1 py-2.5 text-sm font-500 border border-border rounded-xl hover:bg-muted transition-colors disabled:opacity-50"
            >
              Not Yet
            </button>
            <button
              onClick={handleMarkArrived}
              disabled={processing}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-600 bg-primary text-primary-foreground rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {processing ? <><Icon name="ArrowPathIcon" size={14} className="animate-spin" />Confirming…</> : <><Icon name="MapPinIcon" size={14} />Yes, I Have Arrived</>}
            </button>
          </div>
        </div>
      </Modal>

      {/* Approve Fund Release Modal */}
      <Modal
        isOpen={approveFundsOpen}
        onClose={() => setApproveFundsOpen(false)}
        title="Approve Fund Release"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 rounded-xl bg-success/5 border border-success/15">
            <Icon name="BanknotesIcon" size={16} className="text-success flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-600 text-foreground">Release KES {delivery ? Math.round(delivery.offeredPriceKes * 0.95).toLocaleString() : ''} to traveler's M-Pesa?</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                This will immediately release the escrowed funds to the traveler. This action cannot be undone.
              </p>
            </div>
          </div>
          <div className="p-3 rounded-xl bg-muted/50 space-y-2">
            <p className="text-xs font-600 text-foreground">Confirm all parties agree:</p>
            <ul className="text-xs text-muted-foreground space-y-1.5">
              <li className="flex items-center gap-2">
                <Icon name="CheckCircleIcon" size={13} className="text-success flex-shrink-0" />
                Recipient has confirmed receiving the parcel
              </li>
              <li className="flex items-center gap-2">
                <Icon name="CheckCircleIcon" size={13} className="text-success flex-shrink-0" />
                Guarantor ({delivery?.guarantorName || 'guarantor'}) confirms delivery is complete
              </li>
              <li className="flex items-center gap-2">
                <Icon name="CheckCircleIcon" size={13} className="text-success flex-shrink-0" />
                You approve the release of funds
              </li>
            </ul>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setApproveFundsOpen(false)}
              disabled={processing}
              className="flex-1 py-2.5 text-sm font-500 border border-border rounded-xl hover:bg-muted transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleApproveFundRelease}
              disabled={processing}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-600 bg-success text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {processing ? <><Icon name="ArrowPathIcon" size={14} className="animate-spin" />Releasing…</> : <><Icon name="BanknotesIcon" size={14} />Approve & Release Funds</>}
            </button>
          </div>
        </div>
      </Modal>

      {/* Confirm Ready Modal */}
      <Modal
        isOpen={confirmReadyOpen}
        onClose={() => setConfirmReadyOpen(false)}
        title="Confirm Parcel Ready"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 rounded-xl bg-primary/5 border border-primary/15">
            <Icon name="ArchiveBoxIcon" size={16} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-600 text-foreground">Ready to escrow payment?</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                KES {delivery.offeredPriceKes.toLocaleString()} will be held in escrow. The traveler will be notified to pick up the parcel.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setConfirmReadyOpen(false)}
              disabled={processing}
              className="flex-1 py-2.5 text-sm font-500 border border-border rounded-xl hover:bg-muted transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmReady}
              disabled={processing}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-600 bg-primary text-primary-foreground rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {processing ? <><Icon name="ArrowPathIcon" size={14} className="animate-spin" />Processing…</> : <><Icon name="CheckCircleIcon" size={14} />Confirm Ready</>}
            </button>
          </div>
        </div>
      </Modal>

      {/* Cancel Modal */}
      <Modal
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Cancel Delivery"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Are you sure you want to cancel this delivery? The traveler will be notified.
          </p>
          <div>
            <label className="block text-xs font-600 text-foreground mb-1.5">Reason (optional)</label>
            <textarea
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              rows={3}
              placeholder="e.g. Plans changed, no longer need delivery"
              className="w-full px-3 py-2 text-sm border border-border rounded-xl bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setCancelOpen(false)}
              disabled={processing}
              className="flex-1 py-2.5 text-sm font-500 border border-border rounded-xl hover:bg-muted transition-colors disabled:opacity-50"
            >
              Keep Delivery
            </button>
            <button
              onClick={handleCancel}
              disabled={processing}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-600 bg-danger text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {processing ? <><Icon name="ArrowPathIcon" size={14} className="animate-spin" />Cancelling…</> : <><Icon name="XCircleIcon" size={14} />Cancel Delivery</>}
            </button>
          </div>
        </div>
      </Modal>

      {/* Confirm Receipt Modal */}
      <Modal
        isOpen={confirmReceiptOpen}
        onClose={() => { setConfirmReceiptOpen(false); setPinInput(''); setPinError(''); }}
        title="Confirm Receipt & Release Payment"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 rounded-xl bg-success/5 border border-success/15">
            <Icon name="BanknotesIcon" size={16} className="text-success flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-600 text-foreground">Release KES {Math.round(delivery.offeredPriceKes * 0.95).toLocaleString()} to M-Pesa</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Enter the 4-digit delivery PIN to confirm receipt and release escrow payment to the traveler.
              </p>
            </div>
          </div>
          <div>
            <label className="block text-xs font-600 text-foreground mb-1.5">Delivery PIN</label>
            <input
              type="text"
              maxLength={4}
              value={pinInput}
              onChange={e => { setPinInput(e.target.value.replace(/\D/g, '')); setPinError(''); }}
              placeholder="• • • •"
              className="w-full px-3 py-3 rounded-xl border border-input bg-card text-foreground text-center text-2xl font-700 tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {pinError && <p className="text-xs text-danger mt-1.5">{pinError}</p>}
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => { setConfirmReceiptOpen(false); setPinInput(''); setPinError(''); }}
              disabled={processing}
              className="flex-1 py-2.5 text-sm font-500 border border-border rounded-xl hover:bg-muted transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmReceipt}
              disabled={pinInput.length !== 4 || processing}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-600 bg-success text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {processing ? <><Icon name="ArrowPathIcon" size={14} className="animate-spin" />Releasing…</> : <><Icon name="CheckCircleIcon" size={14} />Confirm & Release</>}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
